import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useRouter } from "next/router";

import { formatCurrency } from "../../core/currency.core";
import { getPosUserOrders } from "../../core/posCustomer.core";
import { useTranslation } from "../../core/translation.core";
import { ShopFull } from "../../types/retailShop";
import { PosCustomerInfo } from "../../types/user";
import { EditingMode } from "../reuseables/form.comp";
import { CustomerEditor } from "./customerEditor";
import { useState } from "react";
import { CustomerItem } from "../../types/customer";

interface UserInfoItem {
  title: string;
  value: string;
  hide?: boolean;
}

export default function Customer({
  user,
  customerInfo,
  adminShop,
}: {
  user: CustomerItem;
  customerInfo: PosCustomerInfo | null | undefined;
  adminShop: ShopFull | undefined;
}) {
  const { t, msg } = useTranslation();
  const router = useRouter();

  const { data } = useQuery({
    queryFn: () => getPosUserOrders(user.id),
    queryKey: ["customer", user.id],
  });

  const USER_INFO: UserInfoItem[] = [
    {
      title: t(msg.setting.name),
      value: user.lastName + " " + user.firstName,
    },
    {
      title: t(msg.setting.email),
      value: user.email,
    },
    {
      title: t(msg.customer.mobileNumber),
      value: user.mobileNumber || "-",
    },
    {
      title: t(msg.setting.joinedDate),
      value: format(new Date(user.joined), "dd MMMM yyyy"),
    },
    {
      title: t(msg.customer.remarks),
      value: user.remarks || "-",
      hide: true,
    },
  ];

  if (customerInfo?.profileInfo?.dateOfBirth) {
    USER_INFO.push({
      title: t(msg.setting.dateOfBirth), //
      value: format(new Date(customerInfo!.profileInfo.dateOfBirth), "dd MMMM yyyy"),
    });
  }

  const USER_ORDERS = [
    {
      title: t(msg.pos.totalSpendingAmount),
      value: formatCurrency(data?.totalOrderPrice, "HKD") || "-",
    },
    {
      title: t(msg.pos.lastPurchaseDate),
      value: data?.lastOrder ? format(new Date(data.lastOrder), "dd MMMM yyyy") : "-",
    },
  ];

  function handleClick() {
    router.push(`/customers?id=${user.id}&&edit=customer`);
  }

  function handleCloseEditor() {
    router.push(`/customers?id=${user.id}`);
  }

  return (
    <>
      {router.query.edit !== "customer" ? (
        <section className="space-y-8">
          <div className="space-y-2 leading-loose">
            {USER_INFO.map((info, index) => (
              <Row key={index} info={info} />
            ))}
          </div>

          {adminShop?.customerSpendingInfo && (
            <div className="flex gap-6 flex-wrap">
              {USER_ORDERS.map((order, index) => (
                <div key={index} className="flex-1 p-4 space-y-2 bg-[#EBF2FD] dark:bg-blue-700 dark:text-white rounded-md">
                  <h3 className=" font-medium">{order.title}</h3>
                  <p className="text-lg tracking-wide">{order.value}</p>
                </div>
              ))}
            </div>
          )}

          <button
            id="edit-customer-info"
            onClick={handleClick}
            className="bg-her-blue text-white p-3 text-lg w-full rounded-md shadow-sm flex-center gap-2"
          >
            <span>{pencil}</span>
            <span>{t(msg.customer.editInformation)}</span>
          </button>
        </section>
      ) : (
        <CustomerEditor user={user} mode={EditingMode.edit} handleCloseEditor={handleCloseEditor} />
      )}
    </>
  );
}

function Row({ info }: { info: UserInfoItem }) {
  return (
    <div className="flex justify-between" data-title={info.title}>
      <p className="dark:text-gray-200 opacity-80">{info.title}</p>
      {!info.hide && <p className="whitespace-pre">{info.value}</p>}
      {info.hide && <ShowHideInfo info={info} />}
    </div>
  );
}

function ShowHideInfo({ info }: { info: UserInfoItem }) {
  const { t, msg } = useTranslation();
  const [show, setShow] = useState(false);

  if (show) {
    return (
      <div>
        <button
          onClick={() => {
            setShow(false);
          }}
          type="button"
          id="btn-hide"
          className="text-her-green hover:text-lg"
        >
          {t(msg.customer.hide)}
        </button>
        <p className="whitespace-pre">{info.value}</p>
      </div>
    );
  }

  return (
    <button
      onClick={() => {
        setShow(true);
      }}
      type="button"
      id="btn-show"
      className="text-her-green hover:text-lg"
    >
      {t(msg.customer.show)}
    </button>
  );
}

const pencil = (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.362 3.23725L17.049 1.54925C17.4007 1.19757 17.8777 1 18.375 1C18.8723 1 19.3493 1.19757 19.701 1.54925C20.0527 1.90092 20.2502 2.3779 20.2502 2.87525C20.2502 3.37259 20.0527 3.84957 19.701 4.20125L9.082 14.8202C8.55332 15.3486 7.90137 15.737 7.185 15.9502L4.5 16.7502L5.3 14.0652C5.51328 13.3489 5.90163 12.6969 6.43 12.1682L15.362 3.23725ZM15.362 3.23725L18 5.87525M16.5 12.7502V17.5002C16.5 18.097 16.2629 18.6693 15.841 19.0912C15.419 19.5132 14.8467 19.7502 14.25 19.7502H3.75C3.15326 19.7502 2.58097 19.5132 2.15901 19.0912C1.73705 18.6693 1.5 18.097 1.5 17.5002V7.00025C1.5 6.40351 1.73705 5.83121 2.15901 5.40926C2.58097 4.9873 3.15326 4.75025 3.75 4.75025H8.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
